import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Phone, Lock } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const { login, logout, setActingBranch, refreshUser } = useAuth();
  const { slug } = useParams();
  const isSuperLogin = window.location.pathname === '/super' || window.location.pathname === '/super/login';
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [branchInfo, setBranchInfo] = useState(null);
  const [branchError, setBranchError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API_URL}/api/branches/by-slug/${slug}`)
      .then(res => setBranchInfo(res.data))
      .catch(() => setBranchError('الفرع غير موجود'));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Set acting-branch BEFORE login so the post-login redirect/navigation
      // uses the correct context. Cleared if not on a /b/:slug URL.
      if (slug && !isSuperLogin) {
        setActingBranch(slug);
      } else {
        setActingBranch(null);
      }
      const result = await login(phone, password);
      // /super or /super/login → must be a super_admin account
      if (isSuperLogin && result?.user?.role !== 'super_admin') {
        logout();
        toast.error('هذا الرابط للمدير العام فقط');
        setLoading(false);
        return;
      }
      // /b/:slug → super_admin enters as branch admin via X-Acting-Branch header;
      // non-super users still need to belong to that branch.
      if (branchInfo && result?.user?.role !== 'super_admin' && result?.user?.branch_id !== branchInfo.id) {
        logout();  // clear the just-stored token/auth state to prevent auto-redirect
        toast.error('هذا الحساب لا ينتمي لهذا الفرع');
        setLoading(false);
        return;
      }
      // Super-admin entering a branch → keep acting; route to /dashboard so they see branch UI
      // Super-admin via /super → clear acting; route to /super/branches
      if (result?.user?.role === 'super_admin') {
        if (slug && !isSuperLogin) {
          setActingBranch(slug);
          await refreshUser();  // refetch with X-Acting-Branch so branch context populates correctly
          toast.success(`تم الدخول لفرع ${branchInfo?.name || slug}`);
          navigate('/dashboard');
        } else {
          setActingBranch(null);
          toast.success('تم تسجيل الدخول بنجاح');
          navigate('/super/totals');
        }
      } else {
        toast.success('تم تسجيل الدخول بنجاح');
        navigate(result?.user?.role === 'employee' ? '/sales' : '/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تسجيل الدخول');
    } finally {
      setLoading(false);
    }
  };

  // Branch label: super-admin URL OR root domain → "المنطقة الشرقية"; /b/:slug → branch name
  const subtitle = isSuperLogin
    ? 'المنطقة الشرقية'
    : branchError
      ? branchError
      : (branchInfo?.name || (slug ? '...' : 'المنطقة الشرقية'));

  return (
    <div className="min-h-screen flex flex-col" dir="rtl">
      <div className="flex-1 flex items-center justify-center bg-background p-4 md:p-8">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img
                src="/login-brand.png"
                alt="قِمَم"
                width={88}
                height={88}
                className="object-contain"
                style={{ width: 88, height: 88 }}
                data-testid="brand-logo"
              />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-primary tracking-tight font-brand" data-testid="brand-title">
              قِمَم
            </h1>
            <p className="mt-1 text-primary text-sm font-bold" data-testid="branch-label">
              {subtitle}
            </p>
            <p className="mt-3 text-primary text-xs sm:text-sm font-medium leading-relaxed max-w-sm mx-auto" data-testid="brand-tagline">
              رحلة صعود يبدأها كل موظف .. لتصل الفروع إلى قمّتها الجماعية
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-bold text-primary flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" />
                رقم الجوال
              </Label>
              <Input
                id="phone"
                data-testid="phone-input"
                type="tel"
                placeholder="05xxxxxxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-12 text-right bg-card"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-bold text-primary flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                كلمة المرور
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-right bg-card"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-12 text-lg font-bold"
              disabled={loading}
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </Button>
          </form>

          <div className="text-center pt-4 border-t border-border">
            <div className="credit-footer flex flex-col items-center gap-0" data-testid="credit-footer">
              <p className="text-sm">رؤية وتنفيذ</p>
              <img
                src="/credit-logo.png"
                alt="رؤية وتنفيذ"
                className="h-56 w-56 object-contain -mt-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
